import { createContext, ReactNode, useContext, useRef, useState } from 'react'
import { useSignalEffect } from '@preact/signals-react/runtime'
import { useTranslation } from 'react-i18next'
import { forgotPassword, login, logout } from '@client/utils/db'
import { resetClientState } from '@client/stores/sessionReset'
import Login from './Login'
import ErrorProvider from './ErrorProvider'
//import { useStore, TSettings } from './_StoreProvider'
import tokenService from '@client/utils/tokenService'
import { TSettings, setSettings as settingsSetSettings, getSettings as settingsGetSettings } from '@client/stores/settingsStore'
import { user as authUser, authLoad } from '@client/stores/authStore'
import project from '@client/stores/projectStore'

type AuthProviderProps = { children: ReactNode }
type TAuthContext = {
  handleForgotPassword: (userName: string) => void,
  handleLogin: (serName: string, password: string) => void,
  handleLogout: () => void,
  getSettings: (key: string, defaultValue: any) => any,
  setSettings: (key: string, value: any) => void,
}

const AuthContext = createContext<TAuthContext>({
  handleForgotPassword: () => { },
  handleLogin: () => { },
  handleLogout: () => { },
  getSettings: () => { },
  setSettings: () => { },
})

export function useAuth() {
  return useContext(AuthContext)
}

export default function AuthProvider({ children }: AuthProviderProps) {
  const checkAuthLoad = useRef(true)
  const { i18n } = useTranslation()
  //const { auth, settings, project } = useStore()
  const [currentUser, setCurrentUser] = useState(authUser.value)
  // Signed out without the button: a refused renewal or a 403 clears only the
  // signal. Drawing on with the copy kept here crashed at the next question
  // about rights ("Cannot read properties of null (reading 'role')").
  const signedOut = !!currentUser && !authUser.value
  const currentUserRef = useRef(currentUser)
  currentUserRef.current = currentUser

  //console.log('Loop AuthProvider ...')

  if (!currentUser && checkAuthLoad.current) {
    const result = authLoad()
    if (result) {
      setCurrentUser(result.user)
      handleSettings(result.user._id, result.user.settings)
    }
  }

  async function handleSettings(userId: string, authSettings: TSettings | null) {
    settingsSetSettings(userId, authSettings)
    const lng = settingsGetSettings(userId, 'language')
    if (lng) i18n.changeLanguage(lng)
    const prj = settingsGetSettings(userId, 'openProject')
    if (prj) await project.loadProject(prj)
  }

  async function handleForgotPassword(userName: string) {
    return await forgotPassword(userName)
  }
  async function handleLogin(userName: string, password: string) {
    // Also on the way IN, not just on the way out: a session can end without
    // anyone pressing "log out" (expiry, a cleared token, another tab), and then
    // handleLogout never ran and the previous user's tabs are still open.
    resetClientState()

    const result = await login(userName, password)
    if (result) {
      setCurrentUser(result)
      handleSettings(result._id, result.settings)
    }

    checkAuthLoad.current = true
  }
  async function handleLogout() {
    handleSettings(currentUser._id, null)
    setCurrentUser(null)
    checkAuthLoad.current = false
    // Tabs, sidebars and the cached project data are module-level signals that a
    // logout does not touch, and no page reload happens — so without this the
    // next user to sign in inherits the previous one's open data.
    resetClientState()
    await logout()
  }

  // Followed in an effect, not by subscribing the render: authLoad() below
  // writes the signal while rendering, and a subscribed render would update
  // itself in the middle of it (React warns about exactly that).
  useSignalEffect(() => {
    if (authUser.value || !currentUserRef.current) return
    setCurrentUser(null)
    checkAuthLoad.current = false
    resetClientState()
  })

  function getSettings(key: string, defaultValue: any = false) {
    const userId = currentUser?._id || ''
    return settingsGetSettings(userId, key, defaultValue)
  }
  function setSettings(key: string, value: any) {
    if (currentUser) {
      const userId = currentUser._id
      settingsSetSettings(userId, key, value)
      currentUser.settings = settingsGetSettings(userId)
      tokenService.updateLocalUser(currentUser)
    }
  }

  const value: TAuthContext = {
    handleForgotPassword,
    handleLogin, handleLogout,
    getSettings, setSettings,
  }

  function renderContent() {
    if (currentUser && !signedOut) return children
    return <Login />
  }

  return (
    <ErrorProvider>
      <AuthContext.Provider value={value} >
        {renderContent()}
      </AuthContext.Provider>
    </ErrorProvider>
  )
}
